<template>
  <div>
    <div v-if="loading" class="text-center py-5">
      <b-spinner class="align-middle text-primary" />
    </div>

    <b-alert v-else-if="error" show variant="warning">
      {{ error }}
    </b-alert>

    <template v-else-if="pack">
      <!-- Шапка: информационные поля -->
      <b-card no-body>
        <b-card-header class="pb-50">
          <div class="d-flex justify-content-between align-items-center w-100">
            <h4 class="mb-0">{{ pack.name }}</h4>
            <b-button
                v-if="pack.has_file"
                variant="outline-primary"
                size="sm"
                @click="downloadPack"
            >
              <feather-icon icon="DownloadIcon" class="mr-25" />
              Скачать
            </b-button>
          </div>
        </b-card-header>
        <b-card-body>
          <b-row>
            <b-col cols="6" md="2" class="mb-1">
              <div class="text-muted font-small-3">ID</div>
              <div class="font-weight-bold">{{ pack.id }}</div>
            </b-col>
            <b-col cols="6" md="2" class="mb-1">
              <div class="text-muted font-small-3">Тип</div>
              <div class="font-weight-bold">{{ packTypes[pack.type] || pack.type }}</div>
            </b-col>
            <b-col cols="6" md="2" class="mb-1">
              <div class="text-muted font-small-3">Статус</div>
              <b-badge :variant="statusVariant(pack.status)">{{ statusLabel(pack.status) }}</b-badge>
              <b-badge v-if="pack.is_archived" variant="light-secondary" class="ml-25">Архив</b-badge>
            </b-col>
            <b-col cols="6" md="2" class="mb-1">
              <div class="text-muted font-small-3">Пользователь</div>
              <div class="font-weight-bold">{{ pack.user ? pack.user.name : '—' }}</div>
            </b-col>
            <b-col cols="6" md="2" class="mb-1">
              <div class="text-muted font-small-3">Создан</div>
              <div class="font-weight-bold">{{ formatDate(pack.created_at) }}</div>
            </b-col>
            <b-col cols="6" md="2" class="mb-1">
              <div class="text-muted font-small-3">Обновлён</div>
              <div class="font-weight-bold">{{ formatDate(pack.updated_at) }}</div>
            </b-col>
          </b-row>

          <b-alert v-if="packErrors.length" show variant="danger" class="mt-1">
            <h6 class="alert-heading">Ошибки генерации</h6>
            <div class="alert-body">
              <div v-for="(packError, index) in packErrors" :key="index">{{ packError }}</div>
            </div>
          </b-alert>
        </b-card-body>
      </b-card>

      <!-- Конфигурация -->
      <b-card no-body>
        <b-card-header class="pb-50">
          <h5 class="mb-0">Конфигурация</h5>
        </b-card-header>
        <b-card-body>
          <p v-if="!configRows.length && !themeTables.length" class="text-muted mb-0">
            Конфигурация отсутствует
          </p>

          <b-row>
            <b-col
                v-for="row in configRows"
                :key="row.key"
                cols="6"
                md="3"
                class="mb-1"
            >
              <div class="text-muted font-small-3">{{ row.label }}</div>
              <div class="font-weight-bold">{{ row.value }}</div>
            </b-col>
          </b-row>

          <div v-for="themeTable in themeTables" :key="themeTable.title" class="mt-1">
            <h6>{{ themeTable.title }}</h6>
            <b-table-simple small responsive class="mb-1">
              <b-thead>
                <b-tr>
                  <b-th style="width: 80px">№</b-th>
                  <b-th>Тип темы</b-th>
                  <b-th>Формат</b-th>
                </b-tr>
              </b-thead>
              <b-tbody>
                <b-tr v-for="theme in themeTable.themes" :key="theme.number">
                  <b-td>{{ theme.number }}</b-td>
                  <b-td>{{ theme.type }}</b-td>
                  <b-td>{{ theme.format }}</b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </div>
        </b-card-body>
      </b-card>

      <!-- Содержимое -->
      <b-card no-body>
        <b-card-header class="pb-50">
          <h5 class="mb-0">
            Содержимое<span v-if="contentRows.length" class="text-muted font-small-3 ml-50">({{ contentRows.length }})</span>
          </h5>
        </b-card-header>
        <b-table
            :items="contentRows"
            :fields="contentColumns"
            responsive
            show-empty
            empty-text="Содержимое отсутствует"
            primary-key="number"
            class="position-relative"
        />
      </b-card>
    </template>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCol,
  BRow,
  BSpinner,
  BTable,
  BTableSimple,
  BTbody,
  BTd,
  BTh,
  BThead,
  BTr,
} from 'bootstrap-vue'
import store from '@/store'
import { computed, onUnmounted, ref } from '@vue/composition-api'
import generatorStoreModule from '@/views/generator/generatorStoreModule'
import {
  CLUSTERS,
  LAYOUTS,
  PACK_PRESETS,
  PACK_STATUSES,
  PACK_TYPES,
  THEME_FORMATS,
  THEME_TYPES,
} from '@/views/generator/packDictionaries'

const boolLabel = value => (value ? 'Да' : 'Нет')

// Подписи скалярных опций конфига (источник: generator-front)
const OPTION_LABELS = {
  layout: { label: 'Компоновка', format: value => LAYOUTS[value] || value },
  cluster: { label: 'Кластер', format: value => CLUSTERS[value] || value },
  difficulty: { label: 'Сложность', format: String },
  onlyRu: { label: 'Только RU', format: boolLabel },
  onlyEn: { label: 'Только EN', format: boolLabel },
  addAbbreviationHints: { label: 'Подсказки-аббревиатуры', format: boolLabel },
  addAntiShazam: { label: 'Антишазам', format: boolLabel },
  auctions: { label: 'Вопросы со ставками', format: boolLabel },
  melodyChoiceOptionsCount: { label: 'Вариантов в «1 из N»', format: String },
  covers: { label: 'Темы с каверами (в раунде)', format: String },
  coverArts: { label: 'Темы с обложками (в раунде)', format: String },
  snakes: { label: 'Темы-змейки (в раунде)', format: String },
  finalCovers: { label: 'Кавер-темы финала', format: String },
  finalCoverArts: { label: 'Темы с обложками финала', format: String },
  preset: { label: 'Пресет пакета', format: value => PACK_PRESETS[value] || value },
  finalCount: { label: 'Кол-во тем в финале', format: String },
  frameCount: { label: 'Число кадров', format: String },
  addAbbreviationHintsCommon: { label: 'Подсказки-аббревиатуры', format: boolLabel },
}

const THEME_LIST_LABELS = {
  roundThemes: 'Темы раунда',
  allRoundThemes: 'Темы всех раундов',
  finalThemes: 'Темы финала',
}

export default {
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCol,
    BRow,
    BSpinner,
    BTable,
    BTableSimple,
    BTbody,
    BTd,
    BTh,
    BThead,
    BTr,
  },
  setup(props, { root }) {
    const STORE_MODULE_NAME = 'app-generator'
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, generatorStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const pack = ref(null)
    const loading = ref(true)
    const error = ref(null)

    const contentColumns = [
      { key: 'number', label: '№ п/п', sortable: true },
      { key: 'round', label: 'Раунд', sortable: true },
      { key: 'topic', label: 'Тема', sortable: true },
      { key: 'price', label: 'Стоимость', sortable: true },
      { key: 'question_type', label: 'Формат вопроса', sortable: true },
      { key: 'answer', label: 'Ответ', sortable: true },
    ]

    const options = computed(() => {
      if (!pack.value || !pack.value.config) return {}
      return pack.value.config.options || {}
    })

    const configRows = computed(() => Object.keys(options.value)
      .filter(key => !(key in THEME_LIST_LABELS))
      .map(key => {
        const known = OPTION_LABELS[key]
        const value = options.value[key]
        return {
          key,
          label: known ? known.label : key,
          value: known ? known.format(value) : JSON.stringify(value),
        }
      }))

    const themeTables = computed(() => Object.keys(THEME_LIST_LABELS)
      .filter(key => Array.isArray(options.value[key]) && options.value[key].length)
      .map(key => ({
        title: THEME_LIST_LABELS[key],
        themes: options.value[key].map((theme, index) => ({
          number: index + 1,
          type: THEME_TYPES[theme.type] || theme.type,
          format: THEME_FORMATS[theme.format] || theme.format,
        })),
      })))

    const contentRows = computed(() => (pack.value && pack.value.content_rows) || [])

    const packErrors = computed(() => {
      if (!pack.value || !pack.value.errors) return []
      return Array.isArray(pack.value.errors) ? pack.value.errors : [JSON.stringify(pack.value.errors)]
    })

    const statusLabel = status => (PACK_STATUSES[status] ? PACK_STATUSES[status].label : status)
    const statusVariant = status => (PACK_STATUSES[status] ? PACK_STATUSES[status].variant : 'light-secondary')
    const formatDate = value => (value ? new Date(value).toLocaleString('ru-RU') : '—')

    const downloadPack = () => {
      store.dispatch('app-generator/downloadPack', { id: pack.value.id })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', pack.value.file || `${pack.value.name}.siq`)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
        })
    }

    store.dispatch('app-generator/fetchPackDetails', { id: root.$route.params.id })
      .then(response => {
        pack.value = response.data.data
      })
      .catch(fetchError => {
        error.value = (fetchError.response && fetchError.response.data && fetchError.response.data.message)
          || 'Не удалось загрузить пакет'
      })
      .finally(() => {
        loading.value = false
      })

    return {
      pack,
      loading,
      error,
      contentColumns,
      configRows,
      themeTables,
      contentRows,
      packErrors,
      packTypes: PACK_TYPES,

      statusLabel,
      statusVariant,
      formatDate,
      downloadPack,
    }
  },
}
</script>
